import React, { useState } from "react";
import { X, UserPlus, Send } from "lucide-react";
import api from "../../lib/api";
import { TOURNAMENT, CATEGORY_YEARS } from "../../lib/constants";

const EMPTY = {
    team_name: "",
    category: TOURNAMENT.categories[0],
    city: "",
    manager_name: "",
    email: "",
    phone: "",
    payment_status: "pending_etransfer",
    send_reception_sms: true,
    send_link_sms: true,
    notify_admins: false,
};

export default function ManualRegistrationModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (!open) return null;

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async (e) => {
        e?.preventDefault();
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post("/admin/registrations/manual", form);
            setForm(EMPTY);
            onClose();
            onCreated?.(data);
        } catch (ex) {
            const d = ex?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (Array.isArray(d) ? d.map(x => x.msg).join(", ") : "Erreur lors de la création."));
        } finally {
            setBusy(false);
        }
    };

    const isPaid = form.payment_status === "paid";

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="manual-reg-modal">
            <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
                    <div className="flex items-center gap-2">
                        <UserPlus size={18} className="text-[#7FD93C]" />
                        <h2 className="font-display text-xl uppercase tracking-widest">Nouvelle inscription manuelle</h2>
                    </div>
                    <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="btn-close-manual"><X size={18} /></button>
                </div>

                <form onSubmit={submit} className="p-5 grid gap-4">
                    <div>
                        <label className="label-mini block mb-1">Nom de l&apos;équipe *</label>
                        <input required className="input-field" value={form.team_name} onChange={e => set({ team_name: e.target.value })} data-testid="manual-team-name" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-mini block mb-1">Catégorie *</label>
                            <select required className="input-field" value={form.category} onChange={e => set({ category: e.target.value })} data-testid="manual-category">
                                {TOURNAMENT.categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <div className="text-[10px] text-white/50 mt-1">Né en {CATEGORY_YEARS[form.category]}</div>
                        </div>
                        <div>
                            <label className="label-mini block mb-1">Ville *</label>
                            <input required className="input-field" value={form.city} onChange={e => set({ city: e.target.value })} data-testid="manual-city" />
                        </div>
                    </div>
                    <div>
                        <label className="label-mini block mb-1">Responsable *</label>
                        <input required className="input-field" value={form.manager_name} onChange={e => set({ manager_name: e.target.value })} data-testid="manual-manager" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="label-mini block mb-1">Courriel *</label>
                            <input required type="email" className="input-field" value={form.email} onChange={e => set({ email: e.target.value })} data-testid="manual-email" />
                        </div>
                        <div>
                            <label className="label-mini block mb-1">Téléphone *</label>
                            <input required className="input-field" value={form.phone} onChange={e => set({ phone: e.target.value })} placeholder="514-555-1234" data-testid="manual-phone" />
                        </div>
                    </div>

                    <div className="border-t border-white/10 pt-4">
                        <label className="label-mini block mb-2">Statut du paiement</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => set({ payment_status: "pending_etransfer" })} className={`p-3 angle-tag border text-sm font-display uppercase tracking-wider transition-all ${!isPaid ? "border-[#4FE0D5] bg-[#4FE0D5]/15 text-[#4FE0D5]" : "border-white/10 text-white/60 hover:border-white/30"}`} data-testid="manual-status-pending">
                                En attente Interac
                            </button>
                            <button type="button" onClick={() => set({ payment_status: "paid" })} className={`p-3 angle-tag border text-sm font-display uppercase tracking-wider transition-all ${isPaid ? "border-[#7FD93C] bg-[#7FD93C]/15 text-[#7FD93C]" : "border-white/10 text-white/60 hover:border-white/30"}`} data-testid="manual-status-paid">
                                ✓ Payée
                            </button>
                        </div>
                    </div>

                    <div className="border-t border-white/10 pt-4">
                        <label className="label-mini block mb-2">SMS automatiques</label>
                        <div className="grid gap-2">
                            <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5">
                                <input type="checkbox" checked={form.send_reception_sms} onChange={e => set({ send_reception_sms: e.target.checked })} data-testid="manual-send-reception" />
                                <Send size={14} className="text-[#4FE0D5]" />
                                <span className="text-sm">Envoyer le SMS « Inscription reçue » au responsable</span>
                            </label>
                            {isPaid && (
                                <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5">
                                    <input type="checkbox" checked={form.send_link_sms} onChange={e => set({ send_link_sms: e.target.checked })} data-testid="manual-send-link" />
                                    <Send size={14} className="text-[#7FD93C]" />
                                    <span className="text-sm">Envoyer le SMS avec le lien d&apos;accès (équipe créée auto)</span>
                                </label>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white/5">
                                <input type="checkbox" checked={form.notify_admins} onChange={e => set({ notify_admins: e.target.checked })} data-testid="manual-notify-admins" />
                                <Send size={14} className="text-white/60" />
                                <span className="text-sm text-white/70">Notifier aussi les administrateurs (optionnel)</span>
                            </label>
                        </div>
                    </div>

                    {err && <div className="text-rose-400 text-sm" data-testid="manual-err">{err}</div>}

                    <div className="flex gap-2 justify-end pt-2 border-t border-white/10">
                        <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
                        <button type="submit" disabled={busy} className="btn-primary" data-testid="btn-create-manual">
                            <UserPlus size={14} /> {busy ? "Création..." : "Créer l'inscription"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
